import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getImageGenerationJobStatus, type ImageGenerationJobStatus } from '../../lib/api';

interface ImageJobProgressProps {
  jobId: string;
  onComplete: (status: ImageGenerationJobStatus) => void;
  onError?: (error: string) => void;
}

const POLL_INTERVAL = 3000; // 3 seconds (faster than taxonomy due to shorter generation time)

export default function ImageJobProgress({ jobId, onComplete, onError }: ImageJobProgressProps) {
  const { t } = useTranslation();
  const [jobStatus, setJobStatus] = useState<ImageGenerationJobStatus | null>(null);
  const [error, setError] = useState('');

  // Poll job status
  useEffect(() => {
    if (!jobId) return;

    const pollStatus = async () => {
      try {
        const status = await getImageGenerationJobStatus(jobId);
        setJobStatus(status);

        // Stop polling if completed or failed
        if (status.status === 'completed') {
          setTimeout(() => {
            onComplete(status);
          }, 1500);
        } else if (status.status === 'failed') {
          const errorMsg = status.errorMessage || t('imageGeneration.errors.generationFailed');
          setError(errorMsg);
          onError?.(errorMsg);
        }
      } catch (err: any) {
        console.error('Failed to poll job status:', err);
        const errorMsg = err.response?.data?.error || 'Failed to check job status';
        setError(errorMsg);
        onError?.(errorMsg);
      }
    };

    const interval = setInterval(pollStatus, POLL_INTERVAL);
    pollStatus(); // Poll immediately

    return () => clearInterval(interval);
  }, [jobId, onComplete, onError, t]);

  const getElapsedTime = (): string => {
    if (!jobStatus?.startedAt) return '0s';
    const elapsed = Math.floor((Date.now() - new Date(jobStatus.startedAt).getTime()) / 1000);
    const minutes = Math.floor(elapsed / 60);
    const seconds = elapsed % 60;
    return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
  };

  if (!jobStatus) {
    return (
      <div className="border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 rounded p-6">
        <div className="flex items-center justify-center">
          <div className="w-4 h-4 border-2 border-gray-900 dark:border-white border-t-transparent rounded-full animate-spin"></div>
          <span className="ml-3 text-sm text-gray-600 dark:text-gray-400">
            {t('common.loading')}...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 rounded">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {/* Minimal spinner */}
            {(jobStatus.status === 'pending' || jobStatus.status === 'generating') && (
              <div className="w-4 h-4 border-2 border-gray-900 dark:border-white border-t-transparent rounded-full animate-spin"></div>
            )}
            <h3 className="text-base font-medium text-gray-900 dark:text-white">
              {jobStatus.status === 'pending' && t('imageGeneration.job.pending')}
              {jobStatus.status === 'generating' && t('imageGeneration.job.generating')}
              {jobStatus.status === 'completed' && t('imageGeneration.job.completed')}
              {jobStatus.status === 'failed' && t('imageGeneration.job.failed')}
            </h3>
          </div>
          <div className="flex items-center gap-3">
            {jobStatus.startedAt && (
              <span className="text-sm text-gray-500 dark:text-gray-400 tabular-nums">
                {getElapsedTime()}
              </span>
            )}
          </div>
        </div>

        {/* Progress bar */}
        {(jobStatus.status === 'pending' || jobStatus.status === 'generating') && (
          <div className="mb-4">
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-2">
              <span>
                {t('imageGeneration.job.generatingImages', {
                  current: jobStatus.totalVariantsGenerated,
                  total: jobStatus.totalVariantsRequested,
                })}
              </span>
              <span className="tabular-nums">{jobStatus.progress}%</span>
            </div>
            <div className="w-full h-1 bg-gray-100 dark:bg-gray-900 rounded-full overflow-hidden">
              <div
                className="h-full bg-gray-900 dark:bg-white transition-all duration-500 ease-out"
                style={{ width: `${jobStatus.progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Description */}
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          {jobStatus.status === 'pending' && t('imageGeneration.job.description')}
          {jobStatus.status === 'generating' && t('imageGeneration.job.description')}
          {jobStatus.status === 'completed' && t('imageGeneration.job.completedDescription')}
          {jobStatus.status === 'failed' && (error || t('imageGeneration.job.failedDescription'))}
        </p>

        {/* Progress stats */}
        {(jobStatus.status === 'pending' || jobStatus.status === 'generating') && (
          <div className="flex items-center gap-6 text-sm border-t border-gray-100 dark:border-gray-900 pt-3 mt-3">
            <div className="flex items-center gap-2">
              <span className="text-gray-500 dark:text-gray-400">
                {t('imageGeneration.variants.generated', { defaultValue: 'Generated' })}
              </span>
              <span className="text-gray-900 dark:text-white font-medium tabular-nums">
                {jobStatus.totalVariantsGenerated} / {jobStatus.totalVariantsRequested}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-500 dark:text-gray-400">
                {t('imageGeneration.estimatedTime')}
              </span>
            </div>
          </div>
        )}

        {/* Completed stats - show variants */}
        {jobStatus.status === 'completed' && jobStatus.variants && jobStatus.variants.length > 0 && (
          <div className="border-t border-gray-100 dark:border-gray-900 pt-4 mt-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              {jobStatus.variants.length} {t('imageGeneration.variants.title').toLowerCase()} {t('common.generated', { defaultValue: 'generated' })}
            </div>
            <div className="grid grid-cols-4 gap-3">
              {jobStatus.variants.slice(0, 4).map((variant) => (
                <div
                  key={variant.id}
                  className="aspect-square rounded border-2 border-gray-200 dark:border-gray-800 overflow-hidden"
                >
                  {variant.mediaAsset && (
                    <img
                      src={variant.mediaAsset.storageUrl}
                      alt={`Generated image ${variant.platform}`}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
              ))}
            </div>
            {jobStatus.variants.length > 4 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                +{jobStatus.variants.length - 4} {t('common.more', { defaultValue: 'more' })}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
